import {
  addFiscaisAquisicaoSheet,
  addPartesEnvolvidasSheet,
  addResponsaveisAquisicaoSheet,
  addTermosAditivosSheet,
  addTramitacoesSeiSheet,
  fetchGenericEntityData,
} from './auxiliar_componentes';
import { ArpRelatedData, EnumOption } from '../../../modules/types/types';
import { getAllArpsItens } from '../../../services/contratos/arpsItensService';
import { formatoMoney } from '../../functions';
import { ExportSheet } from '../core';
import { baseLogColumns, DataPreparer } from '../factory';

export class ArpExportPreparer implements DataPreparer {
  private modulo = 'arp';

  /**
   * Busca todos os dados relacionados aos ARPs selecionados
   * Esta função demonstra como usar tanto os services específicos quanto o padrão genérico
   */
  private async fetchRelatedData(arpsIds: number[]): Promise<ArpRelatedData> {
    if (arpsIds.length === 0) return {};

    try {
      const [allItens] = await Promise.all([this.fetchArpsItens()]);
      const itens = this.filterByArpIds(allItens, arpsIds);

      const [
        responsaveisAquisicao,
        fiscaisContrato,
        partesEnvolvidas,
        termosAditivos,
        tramitacoesSei,
      ] = await Promise.all([
        fetchGenericEntityData('responsavelaquisicao', this.modulo, arpsIds),
        fetchGenericEntityData('fiscalaquisicao', this.modulo, arpsIds),
        fetchGenericEntityData('parteenvolvida', this.modulo, arpsIds),
        fetchGenericEntityData('termoaditivo', this.modulo, arpsIds),
        fetchGenericEntityData('tramitacoes', this.modulo, arpsIds),
      ]);

      return {
        itens,
        responsaveisAquisicao,
        fiscaisContrato,
        partesEnvolvidas,
        termosAditivos,
        tramitacoesSei,
      };
    } catch (error) {
      console.error('Erro ao buscar dados relacionados do Arp:', error);
      return {};
    }
  }

  private async fetchArpsItens(): Promise<any[]> {
    try {
      const result = await getAllArpsItens();
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error('Erro ao buscar fases do Arp:', error);
      return [];
    }
  }
  private filterByArpIds(data: any[], arpsIds: number[]): any[] {
    if (!Array.isArray(data) || data.length === 0) return [];

    return data.filter((item) => {
      // Acessa o id do Arp através de duas formas possíveis
      const arpId = item.arp?.id || item.arpId;
      return arpsIds.includes(arpId);
    });
  }

  async prepareForExport(
    selectedArps: any[],
    additionalData?: {
      componenteAf?: EnumOption[];
      statusArp?: EnumOption[];
      tipoCotaArp?: EnumOption[];
      leiLicitacao?: EnumOption[];
    },
  ): Promise<ExportSheet[]> {
    const sheets: ExportSheet[] = [];
    const arpsIds = selectedArps.map((p) => p.id);

    const {
      componenteAf = [],
      statusArp = [],
      tipoCotaArp = [],
      leiLicitacao = [],
    } = additionalData || {};

    // 1. Sheet principal - Atas de Registro de Preço (sempre presente)
    sheets.push({
      name: 'Atas de Registro de Preço',
      data: selectedArps.map((arp) =>
        this.transformArp(arp, componenteAf, statusArp, leiLicitacao),
      ),
      columns: {
        ...baseLogColumns,

        statusArp: 'Status ARP',
        fornecedorNomeFantasia: 'Fornecedor',
        leiLicitacao: 'Lei de Licitação',
        numeroSeiArp: 'Processo Aquisitivo',
        numeroArp: 'Nº ARP',
        componenteAf: 'Componente AF',
        dataPublicacao: 'Início da Vigência',
        dataVigencia: 'Final da Vigência',
        prazoVigencia: 'Prazo',
        itemAdquirido: 'Denominação Genérica',
        valorTotal: 'Valor Total R$',
        nItens: 'Nº Itens',
      },
    });

    // 2. Buscar dados relacionados
    const relatedData = await this.fetchRelatedData(arpsIds);

    // 3. Adicionar sheets dos dados relacionados (apenas se houver dados)
    this.addItensSheet(sheets, tipoCotaArp, relatedData.itens);

    addPartesEnvolvidasSheet(sheets, this.modulo, relatedData.partesEnvolvidas);
    addTermosAditivosSheet(sheets, this.modulo, relatedData.termosAditivos);
    addResponsaveisAquisicaoSheet(
      sheets,
      this.modulo,
      relatedData.responsaveisAquisicao,
    );
    addFiscaisAquisicaoSheet(sheets, this.modulo, relatedData.fiscaisContrato);
    addTramitacoesSeiSheet(sheets, this.modulo, relatedData.tramitacoesSei);

    return sheets;
  }

  /**
   * Transformação do objeto Arp principal
   */
  private transformArp = (
    arp: any,
    componenteAF: EnumOption[],
    ArpStatus: EnumOption[],
    leiLicitacao: EnumOption[],
  ) => {
    return {
      //logs
      id: arp.id,
      usuarioRegistro: arp.usuarioRegistro?.nomeCompleto || 'N/A',
      dataRegistro: arp.dataRegistro,
      dataUltimaAtualizacao: arp.dataUltimaAtualizacao,
      usuarioAtualizacao: arp.usuarioAtualizacao?.nomeCompleto || 'N/A',
      nEdicoes: arp.nEdicoes || 0,
      nVisualizacoes: arp.nVisualizacoes || 0,
      nVisualizadores: arp.nVisualizadores || 0,

      //módulo
      statusArp: ArpStatus[arp.statusArp]?.label || 'N/A',
      numeroSeiArp: arp.numeroSeiArp || 'N/A',
      numeroArp: arp.numeroArp || 'N/A',
      componenteAf: arp.componenteAf
        ? componenteAF[arp.componenteAf]?.label
        : 'N/A',
      fornecedorNomeFantasia: arp.fornecedorNomeFantasia,
      leiLicitacao: leiLicitacao[arp.leiLicitacao]?.label ?? 'N/A',
      itemAdquirido: arp.itemAdquirido || 'N/A',
      dataPublicacao: arp.dataPublicacao || '',
      dataVigencia: arp.dataPublicacao || '-',
      // dataPublicacao: formatDateSimp(arp.dataPublicacao) || '',
      // dataVigencia: formatDateSimp(arp.dataPublicacao) || '-',
      valorTotal: formatoMoney(arp.valorTotalArp.toFixed(2)),

      //observações
      observacoes: arp.observacoes || '',
    };
  };

  /**
   * Adiciona sheet de Itens se houver dados
   */
  private addItensSheet(
    sheets: ExportSheet[],
    tipoCotaArp: EnumOption[],
    itens?: any[],
  ) {
    if (!itens?.length) return;

    sheets.push({
      name: 'Itens',
      data: itens.map((item: any) => this.transformItem(item, tipoCotaArp)),
      columns: {
        id: 'ID',
        dataRegistro: 'Data de Registro',
        dataAtualizacao: 'Última Atualização',
        dataUltimaVisualizacao: 'Última Visualização',
        dataUltimaAtualizacao: 'Última Atualização',
        nEdicoes: 'Edições',
        nVisualizacoes: 'Visualizações',
        nVisualizadores: 'Visualizadores',
        usuarioRegistro: 'Responsável pelo Registro',
        usuarioAtualizacao: 'Responsável Ult. Atualização',
        usuarioUltimaVisualizacao: 'Responsável Ult. Visualização',
        observacoes: 'Observações',

        //Dados da ARP
        arpId: 'Nº da ARP',
        produtoDafId: 'Produto DAF (Item)',

        //Dados do Item da ARP
        tipoCota: 'Tipo de Cota',
        empateFicto: 'Empate Ficto',
        embalagemFator: 'Fator Embalagem',

        //Quantidades
        qtdAdministrativa: 'Qtd Administrativa',
        qtdEstrategico: 'Qtd Estratégico',
        qtdJudicial: 'Qtd Judicial',
        qtdOutro: 'Qtd Outro',
        qtdTotal: 'Qtd Registrada',

        //Valor
        valorUnitHomologado: 'Valor Homologado R$',
        valorUnitReequilibrio: 'Valor Reequilíbrio R$',
        valorTotal: 'Valor Total R$',

        //Métricas
        medContratos: 'Contratos',
        medQtdContratada: 'Qtd Contratada',
        medValorContratado: 'Valor Contratado',
        medQtdSaldo: 'Qtd Saldo',
        medValorSaldo: 'Valor Saldo',
        medPercentSaldo: '% Saldo',
      },
    });
  }

  private transformItem = (item: any, tipoCotaArp: EnumOption[]) => {
    const valorReequilibrioBin = item.valorReequilibrioBin;
    let valorUnitReequilibrio = 0;
    if (valorReequilibrioBin === 'S') {
      valorUnitReequilibrio =
        parseFloat(
          item.valorUnitReequilibrio.replace('.', '').replace(',', '.'),
        ) / 10;
    }
    return {
      //logs
      id: item.id,
      usuarioRegistro: item.usuarioRegistro?.nomeCompleto || 'N/A',
      dataRegistro: item.dataRegistro,
      dataUltimaAtualizacao: item.dataUltimaAtualizacao,
      usuarioAtualizacao: item.usuarioAtualizacao?.nomeCompleto || 'N/A',
      nEdicoes: item.nEdicoes || 0,
      nVisualizacoes: item.nVisualizacoes || 0,
      nVisualizadores: item.nVisualizadores || 0,

      //Dados da ARP
      arpId: item.arp.numeroArp,
      produtoDafId: item.produtoDAF.produtoFarmaceutico,

      //Dados do Item da ARP
      tipoCota: tipoCotaArp[item.tipoCotaArp].label,
      empateFicto: item.empateFicto == 'N' ? 'Não' : 'Sim',
      embalagemFator: item.embalagemFator ? item.embalagemFator : '0',

      //Quantidades
      qtdAdministrativa: item.qtdAdministrativa ? item.qtdAdministrativa : '0',
      qtdEstrategico: item.qtdEstrategico ? item.qtdEstrategico : '0',
      qtdJudicial: item.qtdJudicial ? item.qtdJudicial : '0',
      qtdOutro: item.qtdOutro ? item.qtdOutro : '0',
      qtdTotal: item.qtdTotal ? item.qtdTotal : '0',

      //Valores
      valorUnitHomologado: item.valorUnitHomologado
        ? item.valorUnitHomologado
        : '',
      valorUnitReequilibrio:
        item.valorReequilibrioBin === 'S' ? valorUnitReequilibrio : '-',
      valorTotal: item.valorTotal,
      medContratos: '-',
      medQtdContratada: '-',
      medValorContratado: '-',
      medQtdSaldo: '-',
      medValorSaldo: '-',
      medPercentSaldo: '-',
      observacoes: item.observacoes || '',
    };
  };
}
